/*
 * Magnetic CTAs (desktop pointer only) — the buttons drift a few px toward the
 * cursor when it's within an 80px radius, then spring back on leave. Paired with
 * the hover fill + position:relative in elome-collection-mobile.css.
 *
 * JS owns the transition string entirely: on enter it's a fast snap for the
 * transform (so the nudge tracks the pointer), on leave it's a slow spring for
 * the transform; both keep background/color on a steady 0.25s so the hover fill
 * never jitters. CSS declares NO transition on these selectors, so the two
 * layers can't clobber each other. After the spring settles the inline
 * transition is cleared so CSS regains control of the resting state.
 *
 * Bails (leaving plain buttons) under reduced motion, on coarse/touch pointers,
 * and below the 750px desktop breakpoint.
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    if (window.innerWidth <= 749) return;

    var RADIUS = 80; // px — cursor must be within this of the centre to pull
    var MAX_X = 6; // px — max horizontal drift
    var MAX_Y = 4; // px — max vertical drift

    var TRANS_ENTER = 'background 0.25s ease, color 0.25s ease, transform 0.1s ease';
    var TRANS_LEAVE = 'background 0.25s ease, color 0.25s ease, transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';

    var targets = document.querySelectorAll(
      '.elome-editorial__cta, .elome-swipe__cta, .banner__box .button--secondary'
    );

    targets.forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        el.style.transition = TRANS_ENTER;
      });

      el.addEventListener('mousemove', function (e) {
        var rect = el.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var x = (dx / RADIUS) * MAX_X;
        var y = (dy / RADIUS) * MAX_Y;
        el.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
      });

      el.addEventListener('mouseleave', function () {
        el.style.transition = TRANS_LEAVE;
        el.style.transform = 'translate(0, 0)';
        window.setTimeout(function () {
          el.style.transition = '';
        }, 700);
      });
    });
  });
})();

/*
 * Collection swipe — mobile carousel (≤749px only).
 *
 * A snap-scrolling card track (sections/elome-collection-swipe.liquid) with a
 * single shared content panel + progress dots + counter beneath it. To loop
 * seamlessly — without the visible scrollLeft "jump" the old debounced reset
 * produced — the REAL cards are cloned TWICE and appended, so the track holds
 * three identical sets:
 *
 *     [ originals ][ clone set A ][ clone set B ]
 *
 * The viewport starts centred on the MIDDLE set (scrollLeft = one set width), so
 * there's a full set of cards to scroll into in either direction. A plain,
 * un-debounced scroll listener watches two thresholds: once the scroll crosses
 * past two set widths (into clone set B) or below half a set (back into the
 * originals), it shifts scrollLeft by exactly one set width via direct
 * assignment. The shift lands on the pixel-identical card one set over, so it is
 * invisible; the handler is idempotent, so the scroll event the assignment may
 * itself fire is a harmless no-op.
 *
 * Dots, counter, active-card highlight and the content panel are all derived
 * from the REAL card index (absolute card index modulo the real count), so the
 * clone sets drive the exact same UI as their originals.
 *
 * Bails on desktop or with no track present.
 */
(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', function () {
    if (window.innerWidth > 749) return;

    var track = document.querySelector('[data-elome-swipe-mobile-track]');
    if (!track) return;

    var container = track.closest('.elome-swipe__mobile') || document;
    var dots = container.querySelectorAll('.elome-swipe__mobile-dot');
    var panel = container.querySelector('[data-elome-swipe-mobile-panel]');
    if (!panel) return;

    var counter = panel.querySelector('.elome-swipe__mobile-counter');
    var head = panel.querySelector('[data-elome-swipe-mobile-head]');
    var body = panel.querySelector('[data-elome-swipe-mobile-body]');
    var cta = panel.querySelector('[data-elome-swipe-mobile-cta]');

    // ── Clean slate ────────────────────────────────────────────────────────
    // Drop any leftover clones / static clone cards inside the track so a
    // re-init can't stack duplicate sets.
    track.querySelectorAll('[data-clone="true"], .elome-swipe__card--static').forEach(function (n) {
      n.parentNode.removeChild(n);
    });

    // The REAL cards: everything left in the track that isn't a clone.
    var realCards = Array.prototype.slice.call(
      track.querySelectorAll('.elome-swipe__mobile-card:not([data-clone])')
    );
    var realCount = realCards.length;
    if (realCount < 2) return; // nothing to loop

    // ── Triple the track: originals + two appended clone sets ──────────────
    for (var pass = 0; pass < 2; pass++) {
      realCards.forEach(function (card) {
        var clone = card.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        clone.classList.remove('is-active');
        track.appendChild(clone);
      });
    }
    var allCards = track.querySelectorAll('.elome-swipe__mobile-card');

    // ── Geometry ───────────────────────────────────────────────────────────
    var gap = parseFloat(getComputedStyle(track).columnGap);
    if (isNaN(gap)) gap = parseFloat(getComputedStyle(track).gap) || 0;

    // Per-card scroll advance, and one full set's width (Σ offsetWidth + gaps).
    var cardWidth = realCards[0].offsetWidth + gap;
    var oneSetWidth = 0;
    realCards.forEach(function (card) {
      oneSetWidth += card.offsetWidth + gap;
    });

    function pad(n) {
      return n < 10 ? '0' + n : '' + n;
    }

    // ── UI sync — everything keyed off the REAL card index ─────────────────
    var lastRealIndex = -1;
    var lastActiveAbs = -1;

    // Highlight the absolute card nearest centre (clones included), so the
    // scale/opacity .is-active state tracks the visible card across wraps.
    function setActiveCard(scrollLeft) {
      var abs = Math.round(scrollLeft / cardWidth);
      if (abs < 0) abs = 0;
      if (abs > allCards.length - 1) abs = allCards.length - 1;
      if (abs === lastActiveAbs) return;
      if (lastActiveAbs > -1 && allCards[lastActiveAbs]) {
        allCards[lastActiveAbs].classList.remove('is-active');
      }
      allCards[abs].classList.add('is-active');
      lastActiveAbs = abs;
    }

    // Dots, counter and the shared panel — driven by the real index only.
    function setRealIndex(realIndex) {
      dots.forEach(function (d) {
        d.classList.remove('is-active');
      });
      var dot = container.querySelector('.elome-swipe__mobile-dot[data-index="' + realIndex + '"]');
      if (dot) dot.classList.add('is-active');

      if (counter) counter.textContent = pad(realIndex + 1) + ' / ' + pad(realCount);

      // Soft-fade the panel: drop opacity, swap text/href, fade back. The real
      // card's data-heading is HTML-escaped in Liquid, so it decodes back to
      // real <em> markup → innerHTML keeps the emphasis.
      var src = realCards[realIndex];
      panel.style.opacity = '0';
      if (head) head.innerHTML = src.getAttribute('data-heading') || '';
      if (body) body.textContent = src.getAttribute('data-body') || '';
      if (cta) cta.setAttribute('href', src.getAttribute('data-href') || cta.getAttribute('href'));
      window.setTimeout(function () {
        panel.style.opacity = '1';
      }, 50);
    }

    // ── Infinite loop — direct scrollLeft shift, no debounce ───────────────
    function onScroll() {
      if (track.scrollLeft >= oneSetWidth * 2) {
        track.scrollLeft -= oneSetWidth;
      } else if (track.scrollLeft < oneSetWidth * 0.5) {
        track.scrollLeft += oneSetWidth;
      }

      var sl = track.scrollLeft;
      setActiveCard(sl);

      var realIndex = Math.floor(sl / cardWidth) % realCount;
      if (realIndex < 0) realIndex += realCount;
      if (realIndex !== lastRealIndex) {
        lastRealIndex = realIndex;
        setRealIndex(realIndex);
      }
    }

    track.addEventListener('scroll', onScroll, { passive: true });

    // ── Start centred on the middle set (no initial fade) ──────────────────
    allCards.forEach(function (c) {
      c.classList.remove('is-active');
    });
    track.scrollLeft = oneSetWidth;
    lastActiveAbs = realCount; // first card of the middle (clone A) set
    allCards[realCount].classList.add('is-active');
    lastRealIndex = 0;
    dots.forEach(function (d, i) {
      d.classList.toggle('is-active', i === 0);
    });
    // Counter + panel already render the first card from Liquid, so nothing
    // else to paint on load.
  });
})();

/*
 * PDP gallery — desktop counter sync (≥750px only).
 *
 * On desktop the gallery is a sticky hero (.elome-gallery__primary) + a scrolling
 * stack of secondary images (.elome-gallery__secondary) — see elome-transitions-product.css.
 * The "01 · 07" counter lives at the bottom of the primary column and is scroll-
 * driven: an IntersectionObserver watches each secondary image (tagged data-index
 * in product-media-gallery.liquid). When a secondary image is at least half in the
 * viewport (threshold 0.5) the numerator becomes its data-index; at the top, on
 * the hero, none qualify and the counter falls back to 01 — so the first image
 * always reads as 01. The total is the full product image count.
 *
 * Mobile is untouched: this whole block bails ≤749px, so the Session-2 click /
 * swipe carousel and its own (separate) counter keep running there.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;
  if (window.innerWidth <= 749) return; // desktop only — mobile keeps click-sync

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function setup() {
    var galleries = document.querySelectorAll('.elome-gallery--enhanced');

    galleries.forEach(function (gallery) {
      // Full product image count = primary hero (1) + every secondary image, so
      // the counter reads "01 · 08" … "07 · 08" rather than topping out at the
      // secondary-only count.
      var total = document.querySelectorAll('.elome-gallery__primary img, .elome-gallery__secondary img').length;
      if (total < 2) return;

      var counter = gallery.querySelector('[data-elome-primary-counter]');
      if (!counter) return;

      var current = 0;
      function render(index) {
        if (index === current) return;
        current = index;
        counter.textContent = pad(index) + ' · ' + pad(total);
      }

      // The hero (primary column) always reads as 01.
      render(1);

      // Only the secondary-column images drive the numerator.
      var secondaryItems = gallery.querySelectorAll('.elome-gallery__secondary .product__media-item');
      if (!secondaryItems.length) return; // hero-only product → stays 01

      var ratios = new WeakMap();

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
          });

          // Most-visible secondary image past the 50% line sets the numerator;
          // if none qualifies we're on the hero at the top → 01.
          var best = null;
          var bestRatio = 0;
          secondaryItems.forEach(function (item) {
            var r = ratios.get(item) || 0;
            if (r > bestRatio) {
              bestRatio = r;
              best = item;
            }
          });

          if (best && bestRatio >= 0.5) {
            var index = parseInt(best.getAttribute('data-index'), 10);
            if (!isNaN(index)) render(index);
          } else {
            render(1);
          }
        },
        // threshold 0.5 → fires once an image is at least half in the viewport.
        { root: null, rootMargin: '0px', threshold: 0.5 }
      );

      secondaryItems.forEach(function (item) {
        observer.observe(item);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();

